// ============================================================================
// CrewAhead Auth Helpers
// Centralized onboarding completion check and post-auth redirect logic.
// ============================================================================

import type { User } from '../types/models';
import { isSafeRedirect } from './redirect-utils';
import { isRoleAllowedForPath } from './route-guards';

const ONBOARDING_PATHS = ['/step-1', '/step-2', '/step-3', '/onboarding'];

function isOnboardingPath(path: string): boolean {
  return ONBOARDING_PATHS.some(
    (p) => path === p || path.startsWith(`${p}/`)
  );
}

/**
 * Safely check whether a user has completed onboarding.
 *
 * The backend may return any of three indicators:
 *   - user.onboardingCompleted (boolean, computed from status)
 *   - user.onboarded (boolean, explicit flag)
 *   - user.onboardingStatus === 'completed'
 *
 * This helper checks all three so the frontend never breaks when the
 * backend field name changes.
 */
export function isOnboardingComplete(user: User | null | undefined): boolean {
  if (!user) return false;
  return (
    user.onboardingCompleted === true ||
    user.onboarded === true ||
    user.onboardingStatus === 'completed'
  );
}

/**
 * Get the landing path for a role without considering onboarding state.
 *
 * Rules (canonical routes after dashboard consolidation):
 *   - Admin     → /admin
 *   - Seafarer  → /seafarer
 *   - Shore     → /shore
 *   - Employer  → /employer
 *   - Institute → /institute
 *   - Fallback  → /step-1
 */
export function getRoleLandingPath(role: string | undefined): string {
  const roleRoutes: Record<string, string> = {
    admin: '/admin',
    seafarer: '/seafarer',
    shore: '/shore',
    employer: '/employer',
    institute: '/institute',
  };

  return roleRoutes[role ?? ''] ?? '/step-1';
}


/**
 * Build the correct post-auth redirect URL based on user role and
 * onboarding completion status.
 *
 * Rules (canonical routes after dashboard consolidation):
 *   - Admin                 → /admin
 *   - Institute             → /institute (gate redirects unapproved to /institute/pending)
 *   - Onboarding incomplete → /step-1
 *   - Seafarer (complete)   → /seafarer
 *   - Shore (complete)      → /shore
 *   - Employer (complete)   → /employer
 *   - Institute (complete)  → /institute
 *   - Fallback              → /step-1
 *
 * @param user - The authenticated user object
 * @param justCompleted - Set to true when called right after onboarding
 *                        completion to append ?onboarding=complete
 */
export function getPostAuthRedirect(
  user: User | null | undefined,
  justCompleted = false
): string {
  if (!user) return '/login';

  // Admin bypass — no onboarding required
  if (user.role === 'admin') {
    // eslint-disable-next-line no-console
    console.log('[Auth] Redirecting to: /admin');
    return '/admin';
  }

  // Institute bypass — institutes are approval-gated, not onboarding-gated.
  // Approval status lives on InstituteProfile and is fetched by the
  // InstituteApprovalGate wrapped around /institute routes; unapproved users are
  // redirected from /institute to /institute/pending.
  if (user.role === 'institute') {
    // eslint-disable-next-line no-console
    console.log('[Auth] Redirecting to: /institute (institute role)');
    return '/institute';
  }

  if (!isOnboardingComplete(user)) {
    // eslint-disable-next-line no-console
    console.log('[Auth] Redirecting to: /step-1 (onboarding incomplete)');
    return '/step-1';
  }

  const baseRoute = getRoleLandingPath(user.role);
  const target = justCompleted ? `${baseRoute}?onboarding=complete` : baseRoute;
  // eslint-disable-next-line no-console
  console.log('[Auth] Redirecting to:', target);
  return target;
}

/**
 * Alias for getPostAuthRedirect — semantically clearer after login.
 */
export const getPostLoginRedirect = getPostAuthRedirect;

/**
 * Build a post-login redirect URL that respects a ?redirect= query param
 * only when the target route is authorized for the user's role.
 *
 * This prevents authenticated users from being sent to a role-restricted
 * route (e.g. a seafarer landing on /dashboard/employer) which would
 * immediately bounce them back in a loop.
 */
export function getSafePostLoginRedirect(
  user: User | null | undefined,
  redirect?: string | null
): string {
  if (!user) return '/login';

  // Admin users never belong in the onboarding flow; ignore any onboarding
  // redirect (e.g. /login?redirect=/step-1) and send them straight to /admin.
  if (redirect && user.role === 'admin' && isOnboardingPath(redirect)) {
    // eslint-disable-next-line no-console
    console.log('[Auth] Ignoring onboarding redirect for admin, routing to /admin');
    return '/admin';
  }

  // Institute users are also approval-gated, not onboarding-gated. Ignore
  // onboarding redirects and send them to /institute so the gate can route
  // unapproved users to /institute/pending.
  if (redirect && user.role === 'institute' && isOnboardingPath(redirect)) {
    // eslint-disable-next-line no-console
    console.log('[Auth] Ignoring onboarding redirect for institute, routing to /institute');
    return '/institute';
  }

  if (redirect && isSafeRedirect(redirect) && isRoleAllowedForPath(user.role, redirect)) {
    // eslint-disable-next-line no-console
    console.log('[Auth] Redirecting to safe ?redirect target:', redirect);
    return redirect;
  }

  return getPostAuthRedirect(user);
}

/**
 * Get a human-readable message for the onboarding status.
 */
export function getOnboardingStatusMessage(user: User | null): string {
  if (!user) return 'Please log in to continue.';
  if (isOnboardingComplete(user)) {
    return 'Onboarding complete. Welcome aboard!';
  }
  if (user.onboardingStatus === 'in_progress') {
    return 'Complete the remaining onboarding steps to continue.';
  }
  return 'Welcome! Let\'s get you started with onboarding.';
}
